/**
 * dateRangePresets.ts
 * Quick-range presets for the Master Gantt date window.
 *
 * Each preset computes a {startDate, endDate} pair (local 'YYYY-MM-DD', matching
 * the <input type="date"> value format) relative to a `today` Date. The toolbar
 * renders these in a single-select "Quick range" dropdown; picking one sets both
 * date pickers and applies. Manual edits that don't match any preset show as
 * "Custom range".
 *
 * Week convention is Saturday–Friday (matches the BST timesheets / the locked
 * scheduling-week decision), so "This week" spans the Sat–Fri week of `today`.
 */

/** Local 'YYYY-MM-DD' (NOT UTC) — week/month are human-local concepts, and this
 * matches what the native date input shows the user. */
function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Midnight-local copy with the time stripped. */
function dateOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Most recent Saturday on/before `today` (start of the Sat–Fri week). */
function startOfWeekSaturday(today: Date): Date {
  const d = dateOnly(today);
  // getDay(): Sun=0 … Sat=6. Days since the week's Saturday: Sat→0 … Fri→6.
  const daysSinceSat = (d.getDay() + 1) % 7;
  d.setDate(d.getDate() - daysSinceSat);
  return d;
}

export interface DateRangePreset {
  key: string;
  label: string;
  /** Compute {startDate, endDate} (YYYY-MM-DD) relative to `today`. */
  compute: (today: Date) => { startDate: string; endDate: string };
}

export const SCHEDULE_RANGE_PRESETS: DateRangePreset[] = [
  {
    key: 'thisWeek',
    label: 'This week',
    compute: (today) => {
      const start = startOfWeekSaturday(today);
      const end = new Date(start);
      end.setDate(start.getDate() + 6); // Friday
      return { startDate: toISODate(start), endDate: toISODate(end) };
    },
  },
  {
    key: 'thisMonth',
    label: 'This month',
    compute: (today) => {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0); // last day of month
      return { startDate: toISODate(start), endDate: toISODate(end) };
    },
  },
  {
    key: 'next3Months',
    label: 'Next 3 months',
    compute: (today) => {
      const start = dateOnly(today);
      const end = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());
      return { startDate: toISODate(start), endDate: toISODate(end) };
    },
  },
  {
    key: 'next6Months',
    label: 'Next 6 months',
    compute: (today) => {
      const start = dateOnly(today);
      const end = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate());
      return { startDate: toISODate(start), endDate: toISODate(end) };
    },
  },
  {
    key: 'restOfYear',
    label: 'Through end of year',
    compute: (today) => {
      const start = dateOnly(today);
      const end = new Date(today.getFullYear(), 11, 31); // Dec 31
      return { startDate: toISODate(start), endDate: toISODate(end) };
    },
  },
];

/**
 * BUG-2026-0195: the preset a schedule view opens on when the URL carries no
 * start/end. The Machine Schedule used to fall back to a fixed 2-week window,
 * which the lab found too narrow; it now opens on "Next 3 months". Deriving the
 * default from the preset (rather than a separate day-count) guarantees the
 * Quick-range dropdown labels the fresh view "Next 3 months" via
 * `matchRangePreset` instead of "Custom range".
 */
export const DEFAULT_RANGE_PRESET_KEY = 'next3Months';

/** The default {startDate, endDate} window for `today` (see DEFAULT_RANGE_PRESET_KEY). */
export function computeDefaultRange(today: Date): { startDate: string; endDate: string } {
  const preset =
    SCHEDULE_RANGE_PRESETS.find((p) => p.key === DEFAULT_RANGE_PRESET_KEY) ??
    SCHEDULE_RANGE_PRESETS[0];
  return preset.compute(today);
}

/**
 * Returns the key of the preset whose computed range exactly matches
 * [startDate, endDate], or null when the current window is a custom range.
 */
export function matchRangePreset(
  startDate: string,
  endDate: string,
  today: Date,
): string | null {
  for (const preset of SCHEDULE_RANGE_PRESETS) {
    const r = preset.compute(today);
    if (r.startDate === startDate && r.endDate === endDate) return preset.key;
  }
  return null;
}
